import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, HostListener, ElementRef } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService } from 'shared-components';
import { EtiquetadoService } from '../../../services/etiquetado.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { formatDate } from "@angular/common";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: 'app-mantenimientoDesignaciones-edit',
    templateUrl: './edit.component.html'
})
export class MantenimientoDesignacionesEditComponent implements OnInit, OnDestroy {
    private _designacion;
    public editForm: UntypedFormGroup;
    public showError: boolean;
    public mensajes = new Array<Message>();
    public denominaciones;
    public cultivoId;
    public marcaId;
    public etiquetadorId;

    public porcentajeAforoDisponible = 0;
    public oldKilosADesignar;
    public oldAforoDisponible;

    public etiquetaDigitalModal = false;

    private readonly destroy$ = new Subject<void>();

    @Input() designacionId;
    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _sessionContext: SessionContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _spinnerService: SpinnerService,
        private readonly _etiquetadoService: EtiquetadoService
    ) {}

    ngOnInit() {
        this.editForm = this._fb.group({
            fechaDesignacion: [{ value: '', disabled: true }, Validators.required],
            cultivo: [{ value: '', disabled: true }],
            productor: [{ value: '', disabled: true }],
            cantidad: [{ value: '', disabled: this.getRolePermission() }, Validators.required],
            aforo: [{ value: '', disabled: true }],
            aforoDisponible: [{ value: '', disabled: this.getRolePermission() }],
            impresionAutorizada: [''],
            reimpresionAutorizada: [''],
            finalizada: [''],
            etiquetaDigital: [false]
        });

        this.editForm.get('cantidad').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((kilos) => {
            if (!this.editForm.get('cantidad').pristine) {
              this.editForm.get('aforoDisponible').setValue(this.oldAforoDisponible + (this.oldKilosADesignar - kilos));
              this.porcentajeAforoDisponible = (this.editForm.get('aforoDisponible').value / this.editForm.get('aforo').value) * 100;
            }
          });

        this.getDesignacion();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get claims() {
        return this._sessionContext.sessionUser.lsClaimTypes;
    }

    public get isSmallScreen() {
      return this._sessionContext.isSmallScreen();
    }

    public getDesignacion() {
        this._spinnerService.showSpinner();
        this._etiquetadoService.getDesignacion(this.designacionId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.oldKilosADesignar = resp.cantidad;
            this.oldAforoDisponible = resp.aforoDisponible - resp.cantidad;
            this.porcentajeAforoDisponible = ((resp.aforoDisponible - resp.cantidad) / resp.aforoCultivo) * 100;
            this._designacion = resp;
            this.cultivoId = resp.cultivoId;
            this.marcaId = resp.marcaId;
            this.etiquetadorId = resp.etiquetadorId;
            this.editForm.patchValue(resp);

            this.editForm.patchValue({
                aforo: resp.aforoCultivo,
                aforoDisponible: resp.aforoDisponible,
                cultivo: resp.desFinca,
                productor: resp.desProductor,
                fechaDesignacion: resp.fecha,
                etiquetaDigital: resp.esEtqDigital ?? false
            });
        });
    }

    public close() {
        this.refresh.emit(false);
    }

    public checkIfShowEtiquetaDigitalModal() {
      this.checkForm();
      if (this.editForm.invalid) {
        return;
      }

      // Mostramos el modal solo si se ha marcado el checkbox
      if (this.editForm.get('etiquetaDigital')?.value) {
        this.etiquetaDigitalModal = true;
      } else {
        this.save();
      }
    }

    public save() {
        this.etiquetaDigitalModal = false;
        
        if (this.editForm.valid && this.designacionId) {
            this._designacion = {
                id: this.designacionId,
                cultivoId: this.cultivoId,
                marcaId: this.marcaId,
                etiquetadorId: this.etiquetadorId,
                fecha: formatDate(this.editForm.get('fechaDesignacion').value, 'yyyy-MM-dd', 'en-GB'),
                cantidad: this.editForm.get('cantidad').value,
                impresionAutorizada: this.editForm.get('impresionAutorizada').value,
                finalizada: this.editForm.get('finalizada').value,
                reimpresionAutorizada: this.editForm.get('reimpresionAutorizada').value,
                esEtqDigital: this.editForm.get('etiquetaDigital').value
            };
            this._spinnerService.showSpinner();
            this._etiquetadoService.updateDesignacion(this._designacion).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this.refresh.emit(true);
                this.mensaje.emit('global.success.itemUpdated');
            },
            (error) => {
                    this.showError = true;
                    let message;
                    if (error.status === 420) {
                        switch (error.error.ApiErrorCode) {
                            case 6162: // KilosDesignacionMenorKilosConsumidos - update
                              message = this._translate.instant('ApiErrors.kilosDesignacionMenorKilosConsumidos');
                              break;
                            case 3009: // ItemNoModificablePorRol - update
                              message = this._translate.instant('ApiErrors.itemNoModificablePorRol');
                              break;
                            case 6073: // FechaFueraDeCampanya
                              message = this._translate.instant('ApiErrors.fechaFueraDeCampanya');
                              break;
                            case 3000: // FuncionalidadRestringida
                              message = this._translate.instant('ApiErrors.funcionalidadRestringida');
                              break;
                            case 3014: // EtiquetadorNoAccesible
                              message = this._translate.instant('ApiErrors.etiquetadorNoAccesible');
                              break;
                            case 5100: // CultivoNoAccesible
                              message = this._translate.instant('ApiErrors.cultivoNoAccesible');
                              break;
                            case 6005: // FincaPruebaOpAdmin
                              message =  this._translate.instant('ApiErrors.fincaPruebaOpAdmin');
                              break;
                            case 6006: // CultivoNoAccesibleParaEtiquetadorSeleccionado
                              message = this._translate.instant('ApiErrors.cultivoNoAccesibleParaEtiquetadorSeleccionado');
                              break;
                            case 6036: // CampanyaNoHabilitada
                              message = this._translate.instant('ApiErrors.campanyaHabilitada');
                              break;
                            case 6007: // MarcaPaisDenominacionNoAccesible
                              message = this._translate.instant('ApiErrors.marcaPaisDenominacionNoAccesible');
                              break;
                            case 6008: // CultivoMarcaNoAccesible
                              message = this._translate.instant('ApiErrors.cultivoMarcaNoAccesible');
                              break;
                            case 6009: // MarcaPaisDenominacionNoAccesibleParaEtiquetadorSeleccionado
                              message = this._translate.instant('ApiErrors.marcaPaisDenominacionNoAccesibleParaEtiquetadorSeleccionado');
                              break;
                            case 4007: // FluentValidationGenerico
                              message = this._translate.instant('ApiErrors.fluentValidationGenerico');
                              break;
                            case 5101: // DesignacionNoAccesible
                              message = this._translate.instant('ApiErrors.designacionNoAccesible');
                              break;
                            case 5103: // AlmacenNoAccesible
                              message = this._translate.instant('ApiErrors.almacenNoAccesible');
                              break;
                            case 6074: // DesignacionAlmacenYaExiste
                              message = this._translate.instant('ApiErrors.designacionAlmacenYaExiste');
                              break;
                            case 6097: // DesignacionAlmacenDiferenteEtiquetador
                              message = this._translate.instant('ApiErrors.designacionAlmacenDiferenteEtiquetador');
                              break;
                            default:
                              message = this._translate.instant('ApiErrors.serverError');
                              break;
                        }
                    } else {
                        message = this._translate.instant('ApiErrors.serverError');
                    }
                    this.mensajes.push({ class: 'warning', message: message });
            });
        }
    }

    private checkForm() {
        this.showError = this.editForm.invalid;
        if (this.showError) {
          this.mensajes.push({ class: 'error', message: 'global.error.required' });
        }
    }

    public getRolePermission() {
        let disabled = false;

        if (this.role === 'Administrador') {
            disabled = false;
        } else if (this.role === 'Auditor') {
            disabled = false;
        } else if (this.role === 'Supervisor') {
            disabled = true;
        } else if (this.role === 'Etiquetador') {
            disabled = true;
        }

        return disabled;
    }

}
